import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ClipboardList, Loader, ChevronLeft, Eye, EyeOff,
  CheckCircle, X, Users, BarChart2, Award, Target, Sparkles, ExternalLink,
} from "lucide-react";
import { oeGetExamAttempts, oeGetAttemptDetail, oeGradeAnswer, oeToggleResultVisibility, oeRecalculateAttempt } from "../api/onlineExamApi";
import { lmsGetExamById, lmsBulkIssueCerts } from "../api/lmsApi";
import "../lms.css";

const getUser = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; }
};

const fmtTime = (sec) => {
  if (!sec) return "—";
  const m = Math.floor(sec / 60); const s = sec % 60;
  return `${m}m ${s}s`;
};

/* ── Attempt Row ── */
function AttemptRow({ att, onView, onRecalc, recalcing }) {
  const pf = att.pass_fail;
  return (
    <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 600, color: "#111827" }}>{att.student_name || `Student #${att.student_id}`}</td>
      <td style={{ padding: "10px 14px", fontSize: 12, color: "#6b7280" }}>{att.roll_number || "—"}</td>
      <td style={{ padding: "10px 14px" }}>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
          background: att.status === "submitted" ? "#dbeafe" : att.status === "graded" ? "#dcfce7" : att.status === "in_progress" ? "#fef9c3" : "#f3f4f6",
          color:      att.status === "submitted" ? "#1e40af" : att.status === "graded" ? "#166534" : att.status === "in_progress" ? "#854d0e" : "#374151" }}>
          {att.status}
        </span>
      </td>
      <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "#111827" }}>
        {att.score != null ? `${att.score} / ${att.total_marks}` : "—"}
      </td>
      <td style={{ padding: "10px 14px", fontSize: 13, color: "#374151" }}>{att.percentage != null ? `${att.percentage}%` : "—"}</td>
      <td style={{ padding: "10px 14px" }}>
        {pf && pf !== "pending" ? (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
            background: pf === "pass" ? "#dcfce7" : "#fee2e2",
            color: pf === "pass" ? "#166534" : "#991b1b" }}>
            {pf.toUpperCase()}
          </span>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700 }}>Pending</span>
            <button onClick={() => onRecalc(att)} disabled={recalcing === att.id} style={{
              fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
              border: "1.5px solid #fde68a", background: "#fffbeb", color: "#b45309", cursor: "pointer",
            }}>
              {recalcing === att.id ? "..." : "Fix"}
            </button>
          </div>
        )}
      </td>
      <td style={{ padding: "10px 14px", fontSize: 12, color: "#6b7280" }}>{fmtTime(att.time_spent_sec)}</td>
      <td style={{ padding: "10px 14px", fontSize: 12, color: att.tab_switches > 0 ? "#ef4444" : "#9ca3af" }}>{att.tab_switches || 0}</td>
      <td style={{ padding: "10px 14px" }}>
        <button onClick={() => onView(att)} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          <Eye size={12} /> View
        </button>
      </td>
    </tr>
  );
}

const AUTO_GRADED_TYPES = ["mcq", "true_false", "fill_blank", "match_following"];
const MANUAL_TYPES      = ["long_answer", "long", "short_answer", "short"];

/* ── Grading Modal ── */
function GradingModal({ attempt, onClose, onSaved }) {
  const [answers, setAnswers] = useState([]);
  const [perfSheet, setPerfSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState({});
  const [grades,  setGrades]  = useState({});
  const [tab,     setTab]     = useState("perf"); // "perf" | "auto" | "manual"

  useEffect(() => {
    oeGetAttemptDetail(attempt.id)
      .then(r => {
        const ans = r.data.data.answers || [];
        setAnswers(ans);
        setPerfSheet(r.data.data.performance_sheet || null);
        const init = {};
        ans.forEach(a => { init[a.id] = { marks_awarded: Number(a.marks_awarded) > 0 ? Number(a.marks_awarded) : "", is_correct: a.is_correct }; });
        setGrades(init);
        // Default to manual tab if there are manual questions, otherwise perf
        const hasManual = ans.some(a => MANUAL_TYPES.includes(a.question_type));
        if (hasManual) setTab("manual");
        else setTab("perf");
      })
      .catch(() => toast.error("Failed to load answers"))
      .finally(() => setLoading(false));
  }, [attempt.id]);

  const handleGrade = async (answerId, overrides = {}) => {
    const g = { ...grades[answerId], ...overrides };
    setSaving(p => ({ ...p, [answerId]: true }));
    try {
      await oeGradeAnswer(answerId, {
        marks_awarded: Number(g.marks_awarded) || 0,
        is_correct: g.is_correct,
      });
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Failed to save grade"); }
    finally { setSaving(p => ({ ...p, [answerId]: false })); }
  };

  const autoAnswers   = answers.filter(a => AUTO_GRADED_TYPES.includes(a.question_type));
  const manualAnswers = answers.filter(a => MANUAL_TYPES.includes(a.question_type));

  const autoCorrect = autoAnswers.filter(a => a.is_correct === 1).length;
  const autoWrong   = autoAnswers.filter(a => a.is_correct === 0).length;
  const autoSkipped = autoAnswers.filter(a => !a.option_id && !a.text_answer).length;

  const renderMatchAnswer = (ans) => {
    let colA = [], colB = [], answerKey = [];
    try {
      const p = JSON.parse(ans.question_text || "{}");
      colA      = Array.isArray(p.colA)    ? p.colA    : [];
      colB      = Array.isArray(p.colB)    ? p.colB    : [];
      answerKey = Array.isArray(p.answers) ? p.answers : [];
    } catch {}
    const studentSel = {};
    (ans.text_answer || "").split(",").forEach(part => {
      const m = part.trim().match(/^(\d+)[–\-]([a-z])/i);
      if (m) studentSel[Number(m[1])] = m[2].toLowerCase();
    });
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {/* Correct answer key */}
        <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "12px", border: "1px solid #bbf7d0" }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#166534", marginBottom: 8, letterSpacing: 1 }}>ANSWER KEY</div>
          {colA.map((a, i) => {
            const correctLetter = answerKey[i] || "";
            const correctText   = correctLetter ? colB[correctLetter.charCodeAt(0) - 97] || "" : "";
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, fontSize: 12 }}>
                <span style={{ fontWeight: 600, color: "#374151", minWidth: 80 }}>{i + 1}. {a}</span>
                <span style={{ color: "#9ca3af" }}>→</span>
                <span style={{ background: "#dcfce7", color: "#166534", padding: "1px 8px", borderRadius: 20, fontWeight: 600 }}>{correctLetter}. {correctText}</span>
              </div>
            );
          })}
        </div>
        {/* Student answer */}
        <div style={{ background: "#fffbeb", borderRadius: 8, padding: "12px", border: "1px solid #fde68a" }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#92400e", marginBottom: 8, letterSpacing: 1 }}>STUDENT ANSWER</div>
          {Object.keys(studentSel).length === 0
            ? <em style={{ color: "#9ca3af", fontSize: 12 }}>No answer provided</em>
            : colA.map((a, i) => {
                const num       = i + 1;
                const stuLetter = studentSel[num] || "";
                const stuText   = stuLetter ? colB[stuLetter.charCodeAt(0) - 97] || "" : "";
                const isRight   = stuLetter && stuLetter === (answerKey[i] || "");
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, fontSize: 12 }}>
                    <span style={{ fontWeight: 600, color: "#374151", minWidth: 80 }}>{num}. {a}</span>
                    <span style={{ color: "#9ca3af" }}>→</span>
                    {stuLetter
                      ? <span style={{ background: isRight ? "#dcfce7" : "#fee2e2", color: isRight ? "#166534" : "#991b1b", padding: "1px 8px", borderRadius: 20, fontWeight: 600 }}>
                          {stuLetter}. {stuText} {isRight ? "✓" : "✗"}
                        </span>
                      : <em style={{ color: "#9ca3af" }}>—</em>
                    }
                  </div>
                );
              })
          }
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px", overflow: "auto" }}>
      <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 820, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", overflow: "hidden" }}>

        {/* Gradient top bar */}
        <div style={{ height: 5, background: "linear-gradient(90deg,#4f46e5,#a855f7,#ec4899)" }} />

        {/* Header */}
        <div style={{ padding: "20px 28px 0", display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 46, height: 46, borderRadius: 13, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ClipboardList size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17, color: "#111827" }}>{attempt.student_name || `Student #${attempt.student_id}`}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                Score: <strong style={{ color: "#111827" }}>{attempt.score} / {attempt.total_marks}</strong>
                <span style={{ margin: "0 6px", color: "#d1d5db" }}>|</span>
                {attempt.percentage}%
                <span style={{ margin: "0 6px", color: "#d1d5db" }}>|</span>
                {attempt.tab_switches > 0
                  ? <span style={{ color: "#ef4444", fontWeight: 700 }}>{attempt.tab_switches} tab switches</span>
                  : <span style={{ color: "#9ca3af" }}>No tab switches</span>
                }
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", cursor: "pointer", color: "#6b7280", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={16} />
          </button>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", gap: 0, padding: "16px 28px 0", borderBottom: "1px solid #e5e7eb", marginTop: 16 }}>
          {[
            { key: "perf",   label: "Performance Sheet",                      desc: "Grade · Weak Chapters · Practice" },
            { key: "auto",   label: `Auto-Graded (${autoAnswers.length})`,    desc: "MCQ · T/F · Fill Blank · Match" },
            { key: "manual", label: `Manual Review (${manualAnswers.length})`, desc: "Long Answer · Short Answer" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: "10px 20px 12px", border: "none", background: "none", cursor: "pointer",
              borderBottom: `3px solid ${tab === t.key ? "#4f46e5" : "transparent"}`,
              color: tab === t.key ? "#4f46e5" : "#6b7280",
              fontWeight: tab === t.key ? 700 : 500, fontSize: 13,
              display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2,
            }}>
              <span>{t.label}</span>
              <span style={{ fontSize: 10, color: tab === t.key ? "#a5b4fc" : "#9ca3af", fontWeight: 400 }}>{t.desc}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Loader size={28} className="ms2-spin" color="#4f46e5" />
          </div>
        ) : (
          <div style={{ maxHeight: "62vh", overflow: "auto", padding: "24px 28px" }}>

            {/* ── PERFORMANCE SHEET TAB ── */}
            {tab === "perf" && (
              <div>
                {/* 1. Student Grade & Status */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16,
                  padding: "18px 22px", borderRadius: 14, marginBottom: 20,
                  background: `${perfSheet?.grade_details?.color_code || "#4f46e5"}12`,
                  border: `1.5px solid ${perfSheet?.grade_details?.color_code || "#4f46e5"}40`,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: "50%",
                      background: perfSheet?.grade_details?.color_code || "#4f46e5",
                      display: "flex", alignItems: "center", justifyContent: "center", color: "#fff",
                      fontSize: 22, fontWeight: 900, flexShrink: 0, boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
                    }}>
                      ★
                    </div>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                          Assigned Grade
                        </span>
                        <span style={{
                          fontSize: 15, fontWeight: 900,
                          color: perfSheet?.grade_details?.color_code || "#4f46e5",
                          background: "#fff", padding: "3px 12px", borderRadius: 20,
                          boxShadow: "0 1px 3px rgba(0,0,0,0.06)", border: "1px solid #e2e8f0"
                        }}>
                          {perfSheet?.student_grade || "Evaluated"}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>
                          ({attempt.percentage ?? "—"}%)
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: "#475569", marginTop: 4, fontWeight: 500 }}>
                        {perfSheet?.grade_details?.remarks || "Performance criteria evaluated against configured grading brackets."}
                      </div>
                    </div>
                  </div>

                  {perfSheet?.certificate && (
                    <button
                      onClick={() => window.open(`/student-lms/certificates/${perfSheet.certificate.id}`, "_blank")}
                      style={{
                        display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
                        borderRadius: 8, border: "none", background: "linear-gradient(135deg,#f59e0b,#d97706)",
                        color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(245,158,11,0.25)"
                      }}
                    >
                      <Award size={14} /> View Certificate <ExternalLink size={12} />
                    </button>
                  )}
                </div>

                {/* 2. Weak Chapters Banner */}
                {perfSheet?.weak_chapters && perfSheet.weak_chapters.length > 0 ? (
                  <div style={{
                    padding: "14px 18px", borderRadius: 12, marginBottom: 20,
                    background: "#fef2f2", border: "1.5px solid #fecaca",
                    display: "flex", alignItems: "flex-start", gap: 12,
                  }}>
                    <span style={{ fontSize: 20, lineHeight: 1 }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 800, color: "#991b1b" }}>
                        Needs Focus: {perfSheet.weak_chapters.length} Weak Chapter{perfSheet.weak_chapters.length > 1 ? "s" : ""} Identified (&lt; 50% Accuracy)
                      </div>
                      <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 2 }}>
                        {perfSheet.weak_chapters.map(c => c.chapter_name).join(", ")}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    padding: "12px 18px", borderRadius: 12, marginBottom: 20,
                    background: "#f0fdf4", border: "1.5px solid #bbf7d0",
                    display: "flex", alignItems: "center", gap: 10,
                  }}>
                    <span style={{ fontSize: 18, lineHeight: 1 }}>🎉</span>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>
                      Strong Performance: No weak chapters identified for this student!
                    </div>
                  </div>
                )}

                {/* 3. Chapter-Wise Performance Breakdown */}
                {perfSheet?.chapter_performance && perfSheet.chapter_performance.length > 0 ? (
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: "#1e293b", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                      <Target size={16} color="#4f46e5" />
                      Chapter-Wise Performance & Practice Recommendations
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {perfSheet.chapter_performance.map(chap => {
                        const isWeak = chap.is_weak;
                        return (
                          <div
                            key={chap.chapter_id}
                            style={{
                              border: `1.5px solid ${isWeak ? "#fca5a5" : "#e5e7eb"}`,
                              borderRadius: 12, padding: "14px 18px",
                              background: isWeak ? "#fffbfb" : "#ffffff",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 13.5, fontWeight: 800, color: isWeak ? "#991b1b" : "#1e293b" }}>
                                  {chap.chapter_name}
                                </span>
                                {isWeak ? (
                                  <span style={{ fontSize: 11, fontWeight: 800, background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 12, border: "1px solid #fca5a5" }}>
                                    Weak Chapter
                                  </span>
                                ) : (
                                  <span style={{ fontSize: 11, fontWeight: 700, background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 12 }}>
                                    Strong Chapter
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 13, fontWeight: 800, color: isWeak ? "#dc2626" : "#16a34a" }}>
                                {chap.accuracy_percentage}% Accuracy ({chap.marks_awarded} / {chap.total_marks} marks)
                              </div>
                            </div>

                            {/* Progress bar */}
                            <div style={{ height: 6, background: "#e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 8 }}>
                              <div style={{
                                height: "100%", width: `${Math.min(100, Math.max(0, chap.accuracy_percentage))}%`,
                                background: isWeak ? "linear-gradient(90deg,#ef4444,#f87171)" : "linear-gradient(90deg,#10b981,#34d399)",
                                borderRadius: 10,
                              }} />
                            </div>

                            {/* Recommended practice resources */}
                            {isWeak && chap.recommended_practice?.length > 0 && (
                              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed #fca5a5" }}>
                                <div style={{ fontSize: 12, fontWeight: 800, color: "#991b1b", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
                                  <Sparkles size={13} color="#dc2626" />
                                  Recommended Practice Materials:
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                  {chap.recommended_practice.map(item => (
                                    <a
                                      key={item.id}
                                      href={item.file_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{
                                        display: "inline-flex", alignItems: "center", gap: 5,
                                        fontSize: 11.5, fontWeight: 700, color: "#1d4ed8",
                                        background: "#eff6ff", border: "1px solid #bfdbfe",
                                        padding: "4px 10px", borderRadius: 6, textDecoration: "none"
                                      }}
                                    >
                                      📄 {item.title || "Practice Sheet"}
                                      <ExternalLink size={11} />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 13 }}>
                    No chapter mapping available for questions in this exam.
                  </div>
                )}
              </div>
            )}

            {/* ── AUTO-GRADED TAB ── */}
            {tab === "auto" && (
              <>
                {/* Summary bar */}
                <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                  {[
                    { label: "Correct", value: autoCorrect, bg: "#f0fdf4", color: "#166534", border: "#bbf7d0" },
                    { label: "Wrong",   value: autoWrong,   bg: "#fef2f2", color: "#991b1b", border: "#fecaca" },
                    { label: "Skipped", value: autoSkipped, bg: "#f8fafc", color: "#6b7280",  border: "#e5e7eb" },
                    { label: "Total",   value: autoAnswers.length, bg: "#ede9fe", color: "#4f46e5", border: "#c4b5fd" },
                  ].map(s => (
                    <div key={s.label} style={{ flex: 1, background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: 10, padding: "10px 14px", textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: s.color, opacity: 0.8, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {autoAnswers.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af", fontSize: 14 }}>No auto-graded questions.</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {autoAnswers.map((ans, i) => {
                      const isCorrect = ans.is_correct === 1;
                      const skipped   = !ans.option_id && !ans.text_answer;
                      return (
                        <div key={ans.id} style={{ border: `1.5px solid ${skipped ? "#e5e7eb" : isCorrect ? "#bbf7d0" : "#fecaca"}`, borderRadius: 12, overflow: "hidden" }}>
                          {/* Card header */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: skipped ? "#f8fafc" : isCorrect ? "#f0fdf4" : "#fef2f2", borderBottom: "1px solid #e5e7eb" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ width: 26, height: 26, borderRadius: 8, background: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                                Q{i + 1}
                              </span>
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>
                                {ans.section_label} · {ans.question_type?.replace(/_/g, " ").toUpperCase()}
                              </span>
                              <span style={{ fontSize: 11, color: "#9ca3af" }}>[{ans.question_marks} marks]</span>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 800, padding: "3px 10px", borderRadius: 20,
                              background: skipped ? "#f3f4f6" : isCorrect ? "#dcfce7" : "#fee2e2",
                              color: skipped ? "#6b7280" : isCorrect ? "#166534" : "#991b1b" }}>
                              {skipped ? "Skipped" : isCorrect ? "✓ Correct" : "✗ Wrong"}
                            </span>
                          </div>
                          {/* Question + answer */}
                          <div style={{ padding: "12px 16px" }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginBottom: 10, lineHeight: 1.6 }}>
                              {ans.question_type === "match_following"
                                ? (() => { try { return JSON.parse(ans.question_text || "{}").instruction || "Match the Following"; } catch { return ans.question_text; } })()
                                : ans.question_text}
                            </div>
                            {ans.question_type === "match_following"
                              ? renderMatchAnswer(ans)
                              : (
                                <div style={{ display: "flex", gap: 10 }}>
                                  {ans.selected_option_text && (
                                    <div style={{ flex: 1, background: "#f8fafc", borderRadius: 8, padding: "8px 12px", fontSize: 13, border: "1px solid #e5e7eb" }}>
                                      <span style={{ fontSize: 10, fontWeight: 800, color: "#6b7280", display: "block", marginBottom: 4 }}>STUDENT ANSWER</span>
                                      <span style={{ color: "#374151" }}>{ans.selected_option_text || ans.text_answer || <em style={{ color: "#9ca3af" }}>No answer</em>}</span>
                                    </div>
                                  )}
                                  {ans.text_answer && !ans.selected_option_text && (
                                    <div style={{ flex: 1, background: "#f8fafc", borderRadius: 8, padding: "8px 12px", fontSize: 13, border: "1px solid #e5e7eb" }}>
                                      <span style={{ fontSize: 10, fontWeight: 800, color: "#6b7280", display: "block", marginBottom: 4 }}>STUDENT ANSWER</span>
                                      <span style={{ color: "#374151" }}>{ans.text_answer}</span>
                                    </div>
                                  )}
                                  {ans.correct_option_text && !isCorrect && (
                                    <div style={{ flex: 1, background: "#f0fdf4", borderRadius: 8, padding: "8px 12px", fontSize: 13, border: "1px solid #bbf7d0" }}>
                                      <span style={{ fontSize: 10, fontWeight: 800, color: "#166534", display: "block", marginBottom: 4 }}>CORRECT ANSWER</span>
                                      <span style={{ color: "#166534", fontWeight: 600 }}>{ans.correct_option_text}</span>
                                    </div>
                                  )}
                                </div>
                              )
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {/* ── MANUAL REVIEW TAB ── */}
            {tab === "manual" && (
              <>
                {manualAnswers.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af" }}>
                    <ClipboardList size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <div style={{ fontSize: 14 }}>No long/short answer questions to review.</div>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                    {manualAnswers.map((ans, i) => {
                      const graded = grades[ans.id];
                      const alreadySaved = ans.marks_awarded != null;
                      return (
                        <div key={ans.id} style={{ border: "1.5px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
                          {/* Card header */}
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 18px", background: "linear-gradient(135deg,#4f46e508,#7c3aed08)", borderBottom: "1px solid #e5e7eb" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                                Q{i + 1}
                              </span>
                              <div>
                                <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>{ans.section_label}</span>
                                <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>{ans.question_type?.replace(/_/g, " ").toUpperCase()}</span>
                              </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 12, color: "#9ca3af" }}>Max: <strong style={{ color: "#374151" }}>{ans.question_marks}</strong> marks</span>
                              {alreadySaved && (
                                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: "#dbeafe", color: "#1e40af" }}>
                                  Graded: {ans.marks_awarded}
                                </span>
                              )}
                            </div>
                          </div>

                          <div style={{ padding: "16px 18px" }}>
                            {/* Question */}
                            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", marginBottom: 14, lineHeight: 1.7 }}>
                              {ans.question_text}
                            </div>

                            {/* Student answer */}
                            <div style={{ background: "#fffbeb", borderRadius: 10, padding: "12px 16px", marginBottom: 16, border: "1.5px solid #fde68a" }}>
                              <div style={{ fontSize: 10, fontWeight: 800, color: "#92400e", marginBottom: 6, letterSpacing: 1 }}>STUDENT ANSWER</div>
                              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
                                {ans.text_answer || <em style={{ color: "#9ca3af" }}>No answer provided</em>}
                              </div>
                            </div>

                            {/* Grading controls */}
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e5e7eb" }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginRight: 4 }}>Mark as:</span>
                              <button onClick={() => {
                                const updated = { ...graded, is_correct: 1 };
                                setGrades(g => ({ ...g, [ans.id]: updated }));
                                handleGrade(ans.id, { is_correct: 1, marks_awarded: graded?.marks_awarded });
                              }} style={{
                                padding: "6px 14px", borderRadius: 8, border: "1.5px solid",
                                borderColor: graded?.is_correct === 1 ? "#16a34a" : "#d1d5db",
                                background: graded?.is_correct === 1 ? "#dcfce7" : "#fff",
                                color: graded?.is_correct === 1 ? "#166534" : "#6b7280",
                                fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                              }}>
                                <CheckCircle size={13} /> Correct
                              </button>
                              <button onClick={() => {
                                const updated = { ...graded, is_correct: 0 };
                                setGrades(g => ({ ...g, [ans.id]: updated }));
                                handleGrade(ans.id, { is_correct: 0, marks_awarded: graded?.marks_awarded });
                              }} style={{
                                padding: "6px 14px", borderRadius: 8, border: "1.5px solid",
                                borderColor: graded?.is_correct === 0 ? "#dc2626" : "#d1d5db",
                                background: graded?.is_correct === 0 ? "#fee2e2" : "#fff",
                                color: graded?.is_correct === 0 ? "#991b1b" : "#6b7280",
                                fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                              }}>
                                <X size={13} /> Wrong
                              </button>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>Marks:</span>
                                <input
                                  type="number" min="0" max={ans.question_marks} step="0.5"
                                  value={Number(graded?.marks_awarded) > 0 ? graded.marks_awarded : ""}
                                  onChange={e => setGrades(g => ({ ...g, [ans.id]: { ...g[ans.id], marks_awarded: e.target.value } }))}
                                  onBlur={() => handleGrade(ans.id)}
                                  placeholder="0"
                                  style={{ width: 70, padding: "6px 10px", borderRadius: 8, border: "1.5px solid #d1d5db", fontSize: 14, fontWeight: 700, textAlign: "center", outline: "none" }}
                                />
                                <span style={{ fontSize: 12, color: "#9ca3af" }}>/ {ans.question_marks}</span>
                                {saving[ans.id] && <Loader size={14} className="ms2-spin" color="#4f46e5" />}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

/* ── MAIN PAGE ── */
export default function OnlineExamResults() {
  const { examId } = useParams();
  const nav = useNavigate();
  const userData = getUser();
  const schoolId = userData?.admin_id || null;

  const [exam,        setExam]        = useState(null);
  const [attempts,    setAttempts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [gradeAtt,    setGradeAtt]    = useState(null);
  const [toggling,    setToggling]    = useState(false);
  const [recalcing,   setRecalcing]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [examRes, attRes] = await Promise.all([
        lmsGetExamById(examId),
        oeGetExamAttempts(examId, { school_id: schoolId }),
      ]);
      setExam(examRes.data);
      setAttempts(attRes.data.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const [publishingCert, setPublishingCert] = useState(false);

  useEffect(() => { load(); }, [examId]);

  const handleToggleVisibility = async () => {
    setToggling(true);
    try {
      const next = exam.allow_result_view ? 0 : 1;
      await oeToggleResultVisibility(examId, { allow_result_view: next });
      setExam(e => ({ ...e, allow_result_view: next }));
      toast.success(next ? "Results published to students" : "Results hidden from students");
    } catch { toast.error("Failed to update visibility"); }
    finally { setToggling(false); }
  };

  const handlePublishCertificates = async () => {
    setPublishingCert(true);
    try {
      const user = getUser();
      const school_id = user.school_id || user.admin_id;
      const res = await lmsBulkIssueCerts({ exam_id: examId, school_id });
      toast.success(res?.data?.message || "Certificates published successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to publish certificates");
    } finally {
      setPublishingCert(false);
    }
  };

  // Stats
  const submitted = attempts.filter(a => a.status === "submitted" || a.status === "graded").length;
  const passed    = attempts.filter(a => a.pass_fail === "pass").length;
  const avgScore  = submitted > 0
    ? (attempts.reduce((s, a) => s + (Number(a.score) || 0), 0) / submitted).toFixed(1)
    : "—";

  return (
    <div className="lms-content-inner" style={{ paddingTop: 0 }}>
      {/* Header */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "12px 18px", marginBottom: 12, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden", position: "relative" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#3b82f6,#8b5cf6,#f59e0b)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 2 }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600 }}>
            <ChevronLeft size={16} /> Back
          </button>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#111827", lineHeight: 1.2 }}>{exam?.name || "Exam Results"}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {exam?.class_name}{exam?.subject_name ? ` · ${exam?.subject_name}` : ""} · Online Exam
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {/* Publish Certificates button */}
            <button
              onClick={handlePublishCertificates}
              disabled={publishingCert}
              title="Generate and issue performance certificates to all evaluated students"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 14px",
                borderRadius: 8,
                border: "1.5px solid #fde68a",
                background: "#fef3c7",
                color: "#b45309",
                fontSize: 12.5,
                fontWeight: 700,
                cursor: publishingCert ? "not-allowed" : "pointer",
                boxShadow: "0 1px 3px rgba(245,158,11,0.15)",
              }}
            >
              {publishingCert ? <Loader size={13} className="ms2-spin" /> : <Award size={14} />}
              {publishingCert ? "Publishing..." : "🏆 Publish Certificates"}
            </button>

            {/* Result visibility toggle */}
            <button onClick={handleToggleVisibility} disabled={toggling} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8,
              border: `1.5px solid ${exam?.allow_result_view ? "#fca5a5" : "#bbf7d0"}`,
              background: exam?.allow_result_view ? "#fef2f2" : "#f0fdf4",
              color: exam?.allow_result_view ? "#dc2626" : "#16a34a",
              fontSize: 12.5, fontWeight: 700, cursor: "pointer",
            }}>
              {exam?.allow_result_view ? <EyeOff size={13} /> : <Eye size={13} />}
              {exam?.allow_result_view ? "Hide Results" : "Publish Results"}
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 10, marginBottom: 12 }}>
        {[
          { label: "Total Attempts", value: attempts.length, icon: Users, color: "#4f46e5" },
          { label: "Submitted",      value: submitted,        icon: CheckCircle, color: "#16a34a" },
          { label: "Passed",         value: passed,           icon: BarChart2,   color: "#0891b2" },
          { label: "Avg Score",      value: avgScore,         icon: ClipboardList, color: "#d97706" },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={{ background: "#fff", borderRadius: 10, padding: "10px 14px", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon size={16} color={color} />
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#111827", lineHeight: 1.1 }}>{value}</div>
              <div style={{ fontSize: 10.5, color: "#9ca3af", fontWeight: 600, marginTop: 2 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Attempts table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #e5e7eb", fontWeight: 700, fontSize: 13, color: "#374151", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Student Attempts ({attempts.length})</span>
        </div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <Loader size={24} className="ms2-spin" color="#4f46e5" />
          </div>
        ) : attempts.length === 0 ? (
          <div style={{ textAlign: "center", padding: "36px 16px", color: "#9ca3af", fontSize: 13 }}>
            <ClipboardList size={32} color="#cbd5e1" style={{ margin: "0 auto 8px" }} />
            <div>No students have attempted this exam yet.</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  {["Student", "Roll No", "Status", "Score", "%", "Pass/Fail", "Time", "Tab Switches", "Action"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#9ca3af", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attempts.map(att => (
                  <AttemptRow key={att.id} att={att} onView={setGradeAtt}
                    recalcing={recalcing}
                    onRecalc={async (a) => {
                      setRecalcing(a.id);
                      try {
                        await oeRecalculateAttempt(a.id);
                        toast.success("Score recalculated");
                        load();
                      } catch { toast.error("Recalculate failed"); }
                      finally { setRecalcing(null); }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Grading Modal */}
      {gradeAtt && (
        <GradingModal
          attempt={gradeAtt}
          onClose={() => setGradeAtt(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}
